"use client";
import * as S from "./styles.ts";
import {
  CtaLink,
  H1,
  H2,
  H4,
  H5,
  H6,
  Image,
  P,
  UniversalLink,
} from "@template/components";
import { IImageProps } from "@/lib/types/components";

interface ICardsSectionComponentProps {
  title?: string;
  description?: string;
  cards?: TCard[];
  inverted?: boolean;
  numbered?: boolean;
  altStyling?: boolean;
}

type TCard = {
  title?: string;
  description?: string;
  spanText?: string;
  image?: IImageProps;
  cta?: {
    href: string;
    label: string;
  };
};

const NumberedCard = ({
  details,
  index,
}: {
  details: TCard;
  index: number;
}) => {
  return (
    <S.NumberedCard>
      <S.NumberedCardTextContainer>
        <H5>{details.title}</H5>
        <P $small={true}>{details.description}</P>
      </S.NumberedCardTextContainer>
      <S.Number>{index + 1}.</S.Number>
    </S.NumberedCard>
  );
};

const Card = ({
  details,
  altStyling,
}: {
  details: TCard;
  altStyling?: boolean;
}) => {
  const isImageCard = !!details.image && !!details.image.src;

  if (isImageCard) {
    return (
      <S.ImageCardGrid>
        <S.ImageCard $altStyling={altStyling}>
          <S.ImageContainer>
            <Image
              {...details.image}
              {...(altStyling
                ? {
                    style: {
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      objectPosition: "center",
                    },
                  }
                : {
                    style: {
                      objectFit: "cover",
                    },
                  })}
            />
          </S.ImageContainer>

          <S.ImageCardTextContainer>
            {details.title && <H4>{details.title}</H4>}
            {details.description && (
              <S.ImageCardDescription>
                <P $small={true}>{details.description}</P>
              </S.ImageCardDescription>
            )}
            {details.cta?.href && (
              <>
                {altStyling ? (
                  <UniversalLink
                    href={details.cta?.href}
                    title={details.cta?.label}
                    styled
                  >
                    {details.cta?.label}
                  </UniversalLink>
                ) : (
                  <CtaLink
                    href={details.cta?.href}
                    linkText={details.cta?.label}
                    color="primary"
                  />
                )}
              </>
            )}
          </S.ImageCardTextContainer>
        </S.ImageCard>
      </S.ImageCardGrid>
    );
  }

  return (
    <S.Card>
      {details.title && (
        <S.Stat>
          <H1 as="h3">{details.title}</H1>
        </S.Stat>
      )}
      {details.description && (
        <H6 as="h4">
          {details.description} <span>{details.spanText}</span>{" "}
        </H6>
      )}
    </S.Card>
  );
};

const CardsSectionComponent = ({
  title,
  description,
  cards,
  inverted = false,
  numbered = false,
  altStyling = false,
}: ICardsSectionComponentProps) => {
  return (
    <S.CardsSectionWrapper $inverted={inverted}>
      <S.CardsSectionContainer>
        <S.HeaderContainer>
          <H2>{title}</H2>
          <P>{description}</P>
        </S.HeaderContainer>
        {!cards.length ? null : numbered ? (
          <S.NumberedCardsContainer>
            {cards.map((card, index) => (
              <NumberedCard
                key={index}
                details={card}
                index={index}
              />
            ))}
          </S.NumberedCardsContainer>
        ) : (
          <S.CardsContainer>
            {cards.map((card, index) => (
              <Card
                key={index}
                details={card}
                altStyling={altStyling}
              />
            ))}
          </S.CardsContainer>
        )}
      </S.CardsSectionContainer>
    </S.CardsSectionWrapper>
  );
};

export default CardsSectionComponent;
export const options = [
  {
    description: "",
    displayName: "CardsSectionComponent",
    methods: [],
    props: {
      title: {
        required: false,
        flowType: { name: "string" },
        description: "",
      },
      description: {
        required: false,
        flowType: { name: "string" },
        description: "",
      },
      cards: {
        required: false,
        flowType: {
          name: "Array",
          elements: [
            {
              name: "signature",
              type: "object",
              raw: "{\n  title: string;\n  description?: string;\n  spanText?: string;\n  image?: IImageProps;\n  cta?: {\n    href: string;\n    label: string;\n  };\n}",
              signature: {
                properties: [
                  {
                    key: "title",
                    value: {
                      name: "string",
                      required: true,
                    },
                  },
                  {
                    key: "description",
                    value: {
                      name: "string",
                      required: false,
                    },
                  },
                  {
                    key: "spanText",
                    value: {
                      name: "string",
                      required: false,
                    },
                  },
                  {
                    key: "image",
                    value: {
                      name: "IImageProps",
                      required: false,
                    },
                  },
                  {
                    key: "cta",
                    value: {
                      name: "signature",
                      type: "object",
                      raw: "{\n  href: string;\n  label: string;\n}",
                      signature: {
                        properties: [
                          {
                            key: "href",
                            value: {
                              name: "string",
                              required: true,
                            },
                          },
                          {
                            key: "label",
                            value: {
                              name: "string",
                              required: true,
                            },
                          },
                        ],
                      },
                      required: false,
                    },
                  },
                ],
              },
            },
          ],
          raw: "TCard[]",
        },
        description: "",
      },
      inverted: {
        required: false,
        flowType: { name: "boolean" },
        description: "",
        defaultValue: { value: "false", computed: false },
      },
      numbered: {
        required: false,
        flowType: { name: "boolean" },
        description: "",
        defaultValue: { value: "false", computed: false },
      },
    },
  },
];
